import { useQuery } from '@tanstack/react-query';
import { leaderboardApi } from '@/api/leaderboard.api';
import { normalizeRows } from '../leaderboard-ranking';
import type { LeaderboardData, ViewMode, Metric, LeaderboardPeriod } from '../leaderboard.types';

export const leaderboardQueryKeys = {
  leaderboard: (viewMode: ViewMode, metric: Metric, language?: string, period?: LeaderboardPeriod) =>
    ['leaderboard', viewMode, metric, language, period] as const,
};

/**
 * The leaderboard routes return a plain array, without a total. Returning 0
 * here made `data.total ?? entries.length` always 0, so the contributor count
 * and the pagination of the per-language board showed nothing.
 */
function extractTotal(result: unknown): number | null {
  if (result && typeof result === 'object' && 'totalUsers' in result) {
    const total = Number((result as { totalUsers: unknown }).totalUsers);
    return Number.isFinite(total) ? total : null;
  }
  return null;
}

export function useLeaderboard(viewMode: ViewMode, metric: Metric, language?: string, period: LeaderboardPeriod = 'all') {
  return useQuery<LeaderboardData>({
    queryKey: leaderboardQueryKeys.leaderboard(viewMode, metric, language, period),
    queryFn: async () => {
      if (viewMode === 'global') {
        const result = await leaderboardApi.getGlobalLeaderboard(metric, 100, period);
        return { entries: normalizeRows(result), total: extractTotal(result) };
      }
      const result = await leaderboardApi.getLanguageLeaderboard(language ?? '', 100, period);
      return { entries: normalizeRows(result), total: extractTotal(result) };
    },
    staleTime: 30_000,
  });
}
